import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Props } from '..';
import AutocompleteMain from '../../../Input/autocomplete/AutocompleteMain';
import { AutocompleteStyles } from '../../../Input/autocomplete/types';
import utils from 'utils';

interface AutocompleteFilterProps extends Props {
  styles?: AutocompleteStyles;
}

const AutocompleteFilter: React.FC<AutocompleteFilterProps> = ({
  filterValue,
  setFilter,
  id,
  Header,
  preFilteredRows,
  multiFilterOptions,
  hasLocalKeys,
  matchingType,
}) => {
  const t = utils.intl.useTranslator();

  const allColumnValues = useMemo(() => {
    return utils.table.buildOptionsByRows(
      preFilteredRows,
      id,
      multiFilterOptions,
      t,
      hasLocalKeys,
      Header,
      matchingType,
    );
  }, [preFilteredRows, id, multiFilterOptions, t, hasLocalKeys, Header, matchingType]);

  // Memoize autocomplete data
  const autocompleteData = useMemo(() => {
    return allColumnValues.map((option) => (multiFilterOptions ? option.label : option.value));
  }, [allColumnValues, multiFilterOptions]);

  const [selected, setSelected] = useState<string[]>(() => {
    // Initialize state only once
    return (filterValue ?? '')?.split?.(',')?.filter(Boolean) ?? [];
  });

  const previousFilterValueRef = useRef(filterValue);

  // Apply filter immediately when dropdown closes
  const applyFilter = useCallback(
    (values: string[]) => {
      if (values.length === 0) {
        setFilter('');
        return;
      }

      let filterValues = values;
      if (multiFilterOptions) {
        filterValues = values.map((displayValue) => {
          const option = allColumnValues.find((opt) => opt.label === displayValue);
          return option ? option.value : displayValue;
        });
      }

      setFilter(filterValues.join(','));
    },
    [allColumnValues, multiFilterOptions, setFilter],
  );

  // Handle dropdown close - apply filter
  const handleDropdownClose = useCallback(() => {
    applyFilter(selected);
  }, [selected, applyFilter]);

  // Optimize filterValue effect - only run when filterValue actually changes
  useEffect(() => {
    if (previousFilterValueRef.current === filterValue) {
      return; // Skip if filterValue hasn't actually changed
    }

    previousFilterValueRef.current = filterValue;

    if (typeof filterValue === 'string') {
      const newSelected = filterValue ? filterValue.split(',').filter(Boolean) : [];

      if (multiFilterOptions && newSelected.length > 0) {
        const displayValues = newSelected.map((value) => {
          const displayValue = multiFilterOptions.get(value);
          return displayValue ?? value;
        });
        setSelected(displayValues);
      } else {
        setSelected(newSelected);
      }
    } else {
      setSelected([]);
    }
  }, [filterValue, multiFilterOptions]);

  // Handle autocomplete change - only update local state, don't apply filter yet
  const handleAutocompleteChange = useCallback((values: string[]) => {
    setSelected(values);
  }, []);

  // Memoize display values transformation
  const displayValues = useMemo(() => {
    if (!multiFilterOptions) return selected;

    return selected.map((value) => {
      const option = allColumnValues.find((opt) => opt.label === value);
      if (option) return value;

      const displayValue = multiFilterOptions.get(value);
      return displayValue ?? value;
    });
  }, [selected, multiFilterOptions, allColumnValues]);

  return (
    <div style={{ width: '100%' }}>
      <AutocompleteMain
        placeholder="select"
        data={autocompleteData}
        value={displayValues}
        onChange={handleAutocompleteChange}
        onClose={handleDropdownClose}
        allowCustomOptions
        small
        usePortal
      />
    </div>
  );
};

export default React.memo(AutocompleteFilter);
